import {
	DEFAULT_ACTIVE_PERIOD,
	calculatePeriodResults,
	formatCurrency,
	getCalculatorSettings,
	getLocalizedCalculatorSettings,
	getSliderBubbleLeft,
	getSliderProgress,
} from './shared';

/**
 * Read slider settings from a rendered range element.
 *
 * @param {HTMLInputElement|null} input Range input element.
 * @return {{min:number,max:number,step:number,start:number}}
 */
const getSliderSettingsFromInput = ( input ) => {

	if ( ! input ) {

		return {
			min: 0,
			max: 0,
			step: 1,
			start: 0,
		};

	}

	return {
		min: Number( input.min ),
		max: Number( input.max ),
		step: Number( input.step ) || 1,
		start: Number( input.value ),
	};

};

/**
 * Update tooltip position and visible slider labels.
 *
 * @param {HTMLElement|null} slider Slider wrapper.
 * @param {HTMLInputElement|null} input Range input element.
 * @param {boolean} isCurrency Whether the value should be formatted as currency.
 * @return {void}
 */
const updateSliderUi = ( slider, input, isCurrency = false ) => {

	if ( ! slider || ! input ) {

		return;

	}

	const value = Number( input.value );
	const min = Number( input.min );
	const max = Number( input.max );
	const progress = getSliderProgress( value, min, max );
	const bubble = slider.querySelector( '[data-wombat-calculator-value]' );
	const minLabel = slider.querySelector( '[data-wombat-calculator-min]' );
	const maxLabel = slider.querySelector( '[data-wombat-calculator-max]' );

	slider.style.setProperty( '--wombat-range-progress', `${ progress }%` );

	if ( bubble ) {

		bubble.textContent = isCurrency ? formatCurrency( value ) : String( value );
		bubble.style.left = getSliderBubbleLeft( progress );

	}

	if ( minLabel ) {

		minLabel.textContent = isCurrency ? formatCurrency( min ) : String( min );

	}

	if ( maxLabel ) {

		maxLabel.textContent = isCurrency ? formatCurrency( max ) : String( max );

	}

};

/**
 * Activate a given period tab and panel.
 *
 * @param {HTMLElement[]} tabs Tab buttons.
 * @param {HTMLElement[]} panels Result panels.
 * @param {string} activePeriod Active period key.
 * @param {boolean} shouldFocus Whether to move focus to the activated tab.
 * @return {void}
 */
const setActivePeriod = ( tabs, panels, activePeriod, shouldFocus = false ) => {

	tabs.forEach( ( tab ) => {

		const isActive = tab.dataset.wombatCalculatorTab === activePeriod;
		tab.classList.toggle( 'is-active', isActive );
		tab.setAttribute( 'aria-selected', isActive ? 'true' : 'false' );
		tab.tabIndex = isActive ? 0 : -1;

		if ( isActive && shouldFocus ) {

			tab.focus();

		}

	} );

	panels.forEach( ( panel ) => {

		const isActive = panel.dataset.wombatCalculatorPanel === activePeriod;
		panel.hidden = ! isActive;
		panel.classList.toggle( 'is-active', isActive );

	} );

};

/**
 * Handle keyboard navigation across period tabs.
 *
 * @param {HTMLElement[]} tabs Tab buttons.
 * @param {HTMLElement[]} panels Result panels.
 * @param {KeyboardEvent} event Keyboard event.
 * @param {number} index Current tab index.
 * @return {void}
 */
const handleTabKeydown = ( tabs, panels, event, index ) => {

	const keyMap = {
		ArrowRight: () => ( index + 1 ) % tabs.length,
		ArrowLeft: () => ( index - 1 + tabs.length ) % tabs.length,
		Home: () => 0,
		End: () => tabs.length - 1,
	};

	if ( ! keyMap[ event.key ] ) {

		return;

	}

	event.preventDefault();

	const nextIndex = keyMap[ event.key ]();
	const nextTab = tabs[ nextIndex ];

	setActivePeriod( tabs, panels, nextTab.dataset.wombatCalculatorTab || DEFAULT_ACTIVE_PERIOD, true );

};

/**
 * Connect a slider input with its visible question text.
 *
 * @param {HTMLInputElement|null} input Range input element.
 * @param {HTMLElement|null} question Question element.
 * @param {string} questionId Unique question element ID.
 * @return {void}
 */
const connectQuestionLabel = ( input, question, questionId ) => {

	if ( ! input ) {

		return;

	}

	const questionText = question?.textContent?.trim();

	if ( ! question || ! questionText ) {

		input.removeAttribute( 'aria-labelledby' );
		return;

	}

	question.id = questionId;
	input.setAttribute( 'aria-labelledby', questionId );

};

document.querySelectorAll( '[data-wombat-calculator="true"]' ).forEach( ( block, blockIndex ) => {

	const callsInput = block.querySelector( '[data-wombat-calculator-range="calls"]' );
	const meetingCostInput = block.querySelector( '[data-wombat-calculator-range="meetingCost"]' );

	if ( ! callsInput || ! meetingCostInput ) {

		return;

	}

	const panels = Array.from( block.querySelectorAll( '[data-wombat-calculator-panel]' ) );

	if ( ! panels.length ) {

		return;

	}

	const tabs = Array.from( block.querySelectorAll( '[data-wombat-calculator-tab]' ) );
	const showTabs = tabs.length > 0;

	const callsSlider = block.querySelector( '[data-wombat-calculator-slider="calls"]' );
	const meetingCostSlider = block.querySelector( '[data-wombat-calculator-slider="meetingCost"]' );
	const callsQuestion = block.querySelector( '[data-wombat-calculator-question="calls"]' );
	const meetingCostQuestion = block.querySelector( '[data-wombat-calculator-question="meetingCost"]' );
	const liveRegion = block.querySelector( '[data-wombat-calculator-live-region]' );

	const localizedSettings = getLocalizedCalculatorSettings();
	const settings = getCalculatorSettings( {
		calls: localizedSettings.calls ?? getSliderSettingsFromInput( callsInput ),
		meetingCost: localizedSettings.meetingCost ?? getSliderSettingsFromInput( meetingCostInput ),
		conversionRate: localizedSettings.conversionRate,
		lossRate: localizedSettings.lossRate,
		multipliers: {
			daily: localizedSettings.multipliers?.daily ?? tabs.find( ( tab ) => tab.dataset.wombatCalculatorTab === 'daily' )?.dataset.wombatCalculatorMultiplier,
			monthly: localizedSettings.multipliers?.monthly ?? tabs.find( ( tab ) => tab.dataset.wombatCalculatorTab === 'monthly' )?.dataset.wombatCalculatorMultiplier,
			yearly: localizedSettings.multipliers?.yearly ?? tabs.find( ( tab ) => tab.dataset.wombatCalculatorTab === 'yearly' )?.dataset.wombatCalculatorMultiplier,
		},
	} );

	callsInput.min = String( settings.calls.min );
	callsInput.max = String( settings.calls.max );
	callsInput.step = String( settings.calls.step );
	callsInput.value = String( settings.calls.start );
	meetingCostInput.min = String( settings.meetingCost.min );
	meetingCostInput.max = String( settings.meetingCost.max );
	meetingCostInput.step = String( settings.meetingCost.step );
	meetingCostInput.value = String( settings.meetingCost.start );

	connectQuestionLabel( callsInput, callsQuestion, `calculator-${ blockIndex }-question-calls` );
	connectQuestionLabel( meetingCostInput, meetingCostQuestion, `calculator-${ blockIndex }-question-meeting-cost` );

	/**
	 * Refresh metric values and slider labels for the current block instance.
	 *
	 * @param {boolean} announce Whether the live region should be updated.
	 * @return {void}
	 */
	const updateResults = ( announce = false ) => {

		const values = {
			calls: Number( callsInput.value ),
			meetingCost: Number( meetingCostInput.value ),
		};

		panels.forEach( ( panel ) => {

			const period = panel.dataset.wombatCalculatorPanel || DEFAULT_ACTIVE_PERIOD;
			const result = calculatePeriodResults( values, settings, period );
			const revenueValue = panel.querySelector( '[data-wombat-calculator-result="revenue"]' );
			const lossValue = panel.querySelector( '[data-wombat-calculator-result="loss"]' );

			if ( revenueValue ) {

				revenueValue.textContent = formatCurrency( result.revenue );

			}

			if ( lossValue ) {

				lossValue.textContent = formatCurrency( result.loss );

			}

		} );

		updateSliderUi( callsSlider, callsInput, false );
		updateSliderUi( meetingCostSlider, meetingCostInput, true );

		if ( announce && liveRegion ) {

			const activePanel = panels.find( ( panel ) => ! panel.hidden ) || panels[ 0 ];
			const activePeriod = activePanel?.dataset.wombatCalculatorPanel || initialPeriod;
			const activePeriodLabel = tabs.find( ( tab ) => tab.dataset.wombatCalculatorTab === activePeriod )?.textContent?.trim() || activePeriod;
			const activeRevenue = activePanel?.querySelector( '[data-wombat-calculator-result="revenue"]' )?.textContent?.trim() || '';
			const activeLoss = activePanel?.querySelector( '[data-wombat-calculator-result="loss"]' )?.textContent?.trim() || '';

			liveRegion.textContent = `${ activePeriodLabel }. ${ activeRevenue }. ${ activeLoss }.`;

		}

	};

	const initialPeriod = block.dataset.wombatCalculatorDefaultPeriod || DEFAULT_ACTIVE_PERIOD;
	if ( showTabs && tabs.length ) {

		const tabsIdBase = `calculator-${ blockIndex }`;

		tabs.forEach( ( tab, index ) => {

			const period = tab.dataset.wombatCalculatorTab || DEFAULT_ACTIVE_PERIOD;
			const panel = panels.find( ( currentPanel ) => currentPanel.dataset.wombatCalculatorPanel === period );
			const tabId = `${ tabsIdBase }-tab-${ period }`;
			const panelId = `${ tabsIdBase }-panel-${ period }`;

			tab.id = tabId;
			tab.setAttribute( 'role', 'tab' );
			tab.setAttribute( 'aria-controls', panelId );

			if ( panel ) {

				panel.id = panelId;
				panel.setAttribute( 'role', 'tabpanel' );
				panel.setAttribute( 'aria-labelledby', tabId );

			}

			tab.addEventListener( 'click', () => {

				setActivePeriod( tabs, panels, period, false );
				updateResults( true );

			} );
			tab.addEventListener( 'keydown', ( event ) => {

				handleTabKeydown( tabs, panels, event, index );

				if ( event.defaultPrevented ) {

					updateResults( true );

				}

			} );

		} );

	}

	[ callsInput, meetingCostInput ].forEach( ( input ) => {

		input.addEventListener( 'input', updateResults );
		input.addEventListener( 'change', () => updateResults( true ) );

	} );

	updateResults();

	if ( showTabs && tabs.length ) {

		setActivePeriod( tabs, panels, initialPeriod, false );
		updateResults( false );

	}

} );
